// src/pages/keyholdingFAQ.jsx
import React from "react";
import { Helmet } from "react-helmet-async";

export default function KeyholdingFAQ() {
  return (
    <div className="min-h-screen bg-[#111111] text-white">
      <Helmet>
        <title>Keyholding FAQ | SeguraTech</title>
        <meta
          name="description"
          content="Frequently asked questions about SeguraTech Keyholding, including identity verification, access rules, recording, call-outs, and pricing."
        />
      </Helmet>

      {/* Page header */}
      <section className="max-w-6xl mx-auto px-4 pt-20 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#C6A564] mb-6">
            Keyholding FAQs
          </h1>
          <p className="text-lg text-white/80">
            Clear answers to common questions about how the Keyholding service works.
          </p>
        </div>
      </section>

      {/* FAQ content */}
      <section className="max-w-6xl mx-auto px-4 pb-24">
        <div className="max-w-3xl mx-auto space-y-10 text-white/80">
          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              What is Keyholding?
            </h2>
            <p>
              Keyholding is a professional service designed to help you regain access to
              your property or vehicle if you are locked out. Your keys are stored securely
              and are used only to provide access to you following identity verification.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              Who can be given access?
            </h2>
            <p>
              Access is provided only to the named contract holder. We do not provide
              access to family members, partners, friends, neighbours, or any third party.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              How is identity verified?
            </h2>
            <p>
              Identity is verified using a layered approach. This includes a reference
              photograph held on file, an account password, and photo ID if required. If
              identity cannot be verified, access will be refused.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              Why is an account password used?
            </h2>
            <p>
              The account password provides an additional layer of security and supports
              identity verification. It does not replace photo verification. If the
              password is forgotten, verification may still be completed using other
              approved methods.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              What happens if I am locked out without my phone?
            </h2>
            <p>
              If you are unable to contact us by phone or message, you may attend our
              business premises in person. Identity verification must still be completed
              before we attend your property, and keys are never released directly.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              Are calls or visits recorded?
            </h2>
            <p>
              Yes. Access-related phone calls may be recorded, and body-worn cameras are
              used during attendances. Recording is always declared and is used for the
              protection of both the customer and SeguraTech.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              How many call-outs are included?
            </h2>
            <p>
              Each agreement includes a limited number of emergency call-outs per month.
              Any additional call-outs are chargeable at a small fixed fee. Usage is logged
              and confirmed to you.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              What if I cannot pay immediately?
            </h2>
            <p>
              If additional charges apply and payment cannot be made immediately, payment
              must be settled within 24 hours of attendance. One extension may be granted.
              Continued non-payment will result in cancellation of the service.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              What happens if identity verification fails?
            </h2>
            <p>
              If identity cannot be verified, access will not be provided. This still
              counts as a call-out, as time and travel have been incurred. The reason will
              always be explained politely and clearly.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              What happens in the event of a breakup or separation?
            </h2>
            <p>
              You must notify us immediately if someone leaves the household or access
              rights change. Access will only ever be provided to the verified contract
              holder. The service may be temporarily suspended if there is a dispute.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              What happens if the contract holder dies?
            </h2>
            <p>
              In the event of death, SeguraTech will cooperate with the appropriate legal
              authority or solicitor acting for the estate. Keys are released only to a
              solicitor upon written authority, are never used to provide access following
              death, and the service terminates once keys are lawfully handed over.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              Can the police request access?
            </h2>
            <p>
              Access may be provided to police only where legally required, such as under a
              warrant or lawful emergency powers. All such requests are logged.
            </p>
          </div>
          
          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              Will I automatically allow access to the police or other emergency services?
            </h2>
            <p>
              No. Under UK law, police and emergency services do not have an automatic right of access to private property.

Access may be granted only where lawful authority exists, such as under a warrant, statutory power, or genuine emergency circumstances. SeguraTech does not provide standing or pre-authorised access to third parties and will not grant access where legal authority is unclear.

Emergency services already have their own legal powers of entry where life or safety is at immediate risk and do not rely on keyholding services for automatic access..
            </p>
          </div>
      

          <div>
            <h2 className="text-xl font-semibold text-[#C6A564] mb-2">
              Can I cancel the service?
            </h2>
            <p>
              Monthly plans require one month’s notice. Annual plans may be cancelled at
              any time but are non-refundable except within the statutory 14-day cooling-off
              period.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
